import { isResource } from '@medplum/core'
import type {
  Coding,
  DocumentReference,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { DESK_ACCESS_TAG_SYSTEM } from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  DOCUMENT_TYPES,
  DocumentTypes,
} from '#src/models/document-references/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  generateUUID,
  isOfType,
  now,
  parseReferenceString,
} from '#src/utils.ts'

export type DeskAccess = Reference<Practitioner> | 'shared'

export class Desk extends ExtendedResource<DocumentReference> {
  static readonly RESOURCE_TYPE = 'DocumentReference'

  static readonly DOCUMENT_TYPE = DOCUMENT_TYPES[DocumentTypes.Desk]

  static isInstance(value: unknown): value is DocumentReference {
    return (
      isResource(value) &&
      isOfType(value, 'DocumentReference') &&
      hasCodeableConcept(value.type, Desk.DOCUMENT_TYPE)
    )
  }

  constructor({
    flowObject,
    author,
    title,
    access,
    subject,
  }: {
    flowObject: CustomReactFlowJsonObject
    author: Reference<Practitioner | Organization>
    title: string
    access?: DeskAccess
    subject?: Reference<Patient>
  }) {
    super({
      status: 'current',
      type: Desk.DOCUMENT_TYPE,
      date: now(),
      content: [
        {
          id: generateUUID(),
          attachment: {
            id: generateUUID(),
            title,
            contentType: 'application/json',
            data: base64Encode(JSON.stringify(flowObject)),
          },
        },
      ],
      author: [author],
      subject,
    })
    this.access = access ?? 'shared'
  }

  get subject(): Reference<Patient> | undefined {
    return this.resource.subject as Reference<Patient> | undefined
  }

  static queryNonParticipantDesks(
    practitioner: Reference<Practitioner>
  ): QueryTypes {
    return {
      status: 'current',
      type: Desk.DOCUMENT_TYPE,
      _filter: `(_tag eq ${DESK_ACCESS_TAG_SYSTEM}|${practitioner.reference} or _tag eq ${DESK_ACCESS_TAG_SYSTEM}|shared or _tag ne ${DESK_ACCESS_TAG_SYSTEM}|)`,
      _sort: '-_lastUpdated',
      _count: '1000',
      'subject:missing': true,
    }
  }

  static queryParticipantDesks(
    participant: Reference<Patient>,
    { practitioner }: { practitioner: Reference<Practitioner> }
  ): QueryTypes {
    return {
      status: 'current',
      type: Desk.DOCUMENT_TYPE,
      _filter: `(_tag eq ${DESK_ACCESS_TAG_SYSTEM}|${practitioner.reference} or _tag eq ${DESK_ACCESS_TAG_SYSTEM}|shared or _tag ne ${DESK_ACCESS_TAG_SYSTEM}|)`,
      _sort: '-_lastUpdated',
      _count: '1000',
      subject: participant,
    }
  }

  /* The desks page's listing without its viewer clause: shared desks and desks
   * carrying no access tag at all. For a reader that answers the same for every
   * caller and so has no viewer to name — orbit-mcp's `list_desks` — a private
   * desk is tagged with the one practitioner allowed to see it, and no such
   * reader can qualify for that clause without serving one person's desk to
   * everyone. */
  static querySharedNonParticipantDesks(): QueryTypes {
    return {
      ...Desk.querySharedDesks(),
      'subject:missing': true,
    }
  }

  static querySharedDesk(id: string): QueryTypes {
    return {
      ...Desk.querySharedDesks(),
      _id: id,
      _count: '1',
    }
  }

  private static querySharedDesks(): QueryTypes {
    return {
      status: 'current',
      type: Desk.DOCUMENT_TYPE,
      _filter: `(_tag eq ${DESK_ACCESS_TAG_SYSTEM}|shared or _tag ne ${DESK_ACCESS_TAG_SYSTEM}|)`,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static queryAll(practitioner: Reference<Practitioner>): QueryTypes {
    return {
      status: 'current',
      type: Desk.DOCUMENT_TYPE,
      _filter: `(_tag eq ${DESK_ACCESS_TAG_SYSTEM}|${practitioner.reference} or _tag eq ${DESK_ACCESS_TAG_SYSTEM}|shared or _tag ne ${DESK_ACCESS_TAG_SYSTEM}|)`,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  @Tag({
    system: DESK_ACCESS_TAG_SYSTEM,
  })
  _access?: Coding

  get access(): DeskAccess {
    const code = this._access?.code
    if (code === 'shared' || !code) {
      return 'shared'
    }
    const reference = parseReferenceString(code)
    invariant(isOfType(reference, 'Practitioner'), 'Invalid access reference')
    return reference
  }

  set access(value: DeskAccess) {
    if (value === 'shared') {
      this._access = {
        code: 'shared',
        system: DESK_ACCESS_TAG_SYSTEM,
      }
      return
    }
    const referenceString = value.reference
    invariant(referenceString, 'Invalid access reference')
    this._access = {
      code: referenceString,
      system: DESK_ACCESS_TAG_SYSTEM,
    }
  }

  get date(): string {
    const date = this.resource.date
    invariant(date, 'Document must have a date')
    return date
  }

  get author(): Reference<Practitioner | Organization> {
    const author = this.resource.author?.[0]
    invariant(
      isOfType(author, 'Practitioner') || isOfType(author, 'Organization'),
      'Document author must be either a Practitioner or Organization'
    )
    return author
  }

  get title(): string {
    const title = this.resource.content?.[0]?.attachment?.title
    invariant(title, 'Document must have a title')
    return title
  }

  set title(title: string) {
    const attachment = this.resource.content?.[0]?.attachment
    invariant(attachment, 'Document must have an attachment')
    attachment.title = title
  }

  get status(): DocumentReference['status'] {
    return this.resource.status
  }

  set status(status: DocumentReference['status']) {
    this.resource.status = status
  }

  get flowObject(): CustomReactFlowJsonObject {
    const attachment = this.resource.content?.[0]?.attachment
    invariant(
      attachment &&
        attachment.contentType === 'application/json' &&
        attachment.data,
      'Document must have a valid JSON attachment'
    )
    return JSON.parse(base64Decode(attachment.data))
  }

  set flowObject(flowObject: CustomReactFlowJsonObject) {
    const attachment = this.resource.content?.[0]?.attachment
    invariant(attachment, 'Document must have an attachment')
    attachment.data = base64Encode(JSON.stringify(flowObject))
  }
}

// This is intended to mirror ReactFlowJsonObject from @xyflow/react but we don't want to import the whole @xyflow/react library and its peers (including react).
interface CustomReactFlowJsonObject {
  nodes: Node[]
  edges: object[] // We don't currently use edges so we just left them as an object for now.
  viewport: Viewport
}

// Node payloads are opaque to core: xyflow hands them back as `Record<string, unknown>`,
// so the default names no fields and stays assignable from xyflow's own nodes.
// A type alias rather than an interface so it stays assignable to `JsonObject`:
// aliases of object literal types get an implicit index signature, interfaces do not.
type DeskNodeData = {
  author?: string
  owner?: string
  createdAt?: string
  date?: string
  referenceString?: string
  access?: string
}

// The types below are based on the corresponding types in @xyflow/react.

type Node<
  NodeData extends object = DeskNodeData,
  NodeType extends string = string,
> = NodeBase<NodeData, NodeType> & {
  style?: any
  className?: string
  resizing?: boolean
  focusable?: boolean
}

type NodeBase<
  NodeData extends object = DeskNodeData,
  NodeType extends string = string,
> = {
  /** Unique id of a node */
  id: string
  /**
   * Position of a node on the pane
   * @example { x: 0, y: 0 }
   */
  position: XYPosition
  /** Arbitrary data passed to a node */
  data: NodeData
  /** Type of node defined in nodeTypes */
  type?: NodeType
  /**
   * Only relevant for default, source, target nodeType. controls source position
   * @example 'right', 'left', 'top', 'bottom'
   */
  sourcePosition?: Position
  /**
   * Only relevant for default, source, target nodeType. controls target position
   * @example 'right', 'left', 'top', 'bottom'
   */
  targetPosition?: Position
  hidden?: boolean
  selected?: boolean
  /** True, if node is being dragged */
  dragging?: boolean
  draggable?: boolean
  selectable?: boolean
  connectable?: boolean
  deletable?: boolean
  dragHandle?: string
  width?: number
  height?: number
  initialWidth?: number
  initialHeight?: number
  /** Parent node id, used for creating sub-flows */
  parentId?: string
  zIndex?: number
  /**
   * Boundary a node can be moved in
   * @example 'parent' or [[0, 0], [100, 100]]
   */
  extent?: 'parent' | CoordinateExtent
  expandParent?: boolean
  ariaLabel?: string
  /**
   * Origin of the node relative to it's position
   * @example
   * [0.5, 0.5] // centers the node
   * [0, 0] // top left
   * [1, 1] // bottom right
   */
  origin?: NodeOrigin
  handles?: NodeHandle[]
  measured?: {
    width?: number
    height?: number
  }
}

type XYPosition = {
  x: number
  y: number
}

type Position = 'left' | 'top' | 'right' | 'bottom'

type CoordinateExtent = [[number, number], [number, number]]

type NodeOrigin = [number, number]

type NodeHandle = Omit<Optional<Handle, 'width' | 'height'>, 'nodeId'>

type Handle = {
  id?: string | null
  nodeId: string
  x: number
  y: number
  position: Position
  type: HandleType
  width: number
  height: number
}

type HandleType = 'source' | 'target'

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

type Viewport = {
  x: number
  y: number
  zoom: number
}
